import type { CSSProperties } from 'react';
import { getScoreColor, getScoreLabelKey } from '../../lib/accessibility-score';
import i18n from '../../i18n/i18n';

export type ScoreCardSize = 'sm' | 'md' | 'lg';

interface ScoreCardProps {
  score: number;
  label?: string;
  size?: ScoreCardSize;
  style?: CSSProperties;
}

const SIZES: Record<ScoreCardSize, { ring: number; num: number; lbl: number }> = {
  sm: { ring: 68, num: 22, lbl: 11 },
  md: { ring: 92, num: 30, lbl: 12 },
  lg: { ring: 118, num: 38, lbl: 13 },
};

export default function ScoreCard({
  score,
  label,
  size = 'md',
  style,
}: ScoreCardProps) {
  const color = getScoreColor(score);
  const text = label ?? i18n.t(`common:${getScoreLabelKey(score)}`);
  const { ring, num, lbl } = SIZES[size];

  const cx = ring / 2;
  const r = cx - 7;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;

  return (
    <div className="acc-score-card" style={style}>
      <div
        className="acc-score-card__ring"
        style={{
          width: ring,
          height: ring,
        }}
      >
        <svg
          width={ring}
          height={ring}
          className="acc-score-card__svg"
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke="rgba(146, 164, 186, 0.2)"
            strokeWidth={7}
          />
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={7}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
            style={{ transition: 'stroke-dasharray 0.4s ease' }}
          />
        </svg>
        <div
          className="acc-score-card__value"
          style={{
            fontSize: num,
            color,
          }}
        >
          {score}
        </div>
      </div>
      <div className="acc-score-card__label" style={{ fontSize: lbl }}>
        {text}
      </div>
    </div>
  );
}
